import { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useTheme,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { services } from '../pages/Sell/services';
import { Scale, AccessTime } from '@mui/icons-material';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

const timeFrames = [
  { id: '1min', label: '۱ دقیقه' },
  { id: '10min', label: '۱۰ دقیقه' },
  { id: '1hour', label: '۱ ساعت' },
  { id: '1day', label: '۱ روز' },
];

const availableWeights = [
  { id: 'BULLION_1', label: '۱ گرم', weight: 1 },
  { id: 'BULLION_2', label: '۲ گرم', weight: 1 },
  { id: 'BULLION_5', label: '۵ گرم', weight: 5 },
  { id: 'BULLION_10', label: '۱۰ گرم', weight: 10 },
  { id: 'BULLION_20', label: '۲۰ گرم', weight: 20 },
  { id: 'BULLION_50', label: '۵۰ گرم', weight: 50 },
  { id: 'BULLION_100', label: '۱۰۰ گرم', weight: 100 },
];

const availableSources = [
  { id: 'SERVAT_NARIN', label: 'ثروت‌افزا-نارین' },
  { id: 'SERVAT_TOKENIKO', label: 'ثروت‌افزا-توکنیکو' },
  { id: 'IME_AVG_ASK_PRICE', label: 'بورس کالا(فروش)' },
  { id: 'IME_AVG_BID_PRICE', label: 'بورس کالا(خرید)' },
  { id: 'ESTJ', label: 'اتحادیه' },
];

export default function GoldPriceChart({
  sources = ['SERVAT_TOKENIKO', 'SERVAT_NARIN', 'ESTJ'],
  title = 'نمودار قیمت',
  hideWeightInput,
  defaultWeight = 'BULLION_1',
}) {
  const [selectedWeight, setSelectedWeight] = useState(defaultWeight);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1day');
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await services.getChartData(
          selectedWeight,
          sources,
          selectedTimeFrame
        );
        setApiData(response);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedWeight, selectedTimeFrame]);

  const handleWeightChange = event => {
    setSelectedWeight(event.target.value);
  };

  const handleTimeFrameChange = event => {
    setSelectedTimeFrame(event.target.value);
  };

  const getChartLabels = () => {
    if (!apiData || !Object.keys(apiData).length) {
      return selectedTimeFrame === '1min'
        ? ['۱۰:۰۰', '۱۰:۰۱', '۱۰:۰۲', '۱۰:۰۳', '۱۰:۰۴', '۱۰:۰۵']
        : selectedTimeFrame === '10min'
          ? ['۱۰:۰۰', '۱۰:۱۰', '۱۰:۲۰', '۱۰:۳۰', '۱۰:۴۰', '۱۰:۵۰']
          : selectedTimeFrame === '1hour'
            ? ['۰۸:۰۰', '۰۹:۰۰', '۱۰:۰۰', '۱۱:۰۰', '۱۲:۰۰', '۱۳:۰۰']
            : ['روز ۱', 'روز ۲', 'روز ۳', 'روز ۴', 'روز ۵', 'روز ۶'];
    }
    const timestamps =
      apiData[Object.keys(apiData)[0]]?.map(entry => {
        const utcDate = new Date(entry.timestamp);
        if (isNaN(utcDate.getTime())) {
          return ['نامعتبر'];
        }

        // const iranOffsetMs = 3.5 * 60 * 60 * 1000;
        const iranOffsetMs = 0;
        const iranDate = new Date(utcDate.getTime() + iranOffsetMs);

        if (
          selectedTimeFrame === '1min' ||
          selectedTimeFrame === '10min' ||
          selectedTimeFrame === '1hour'
        ) {
          const time = iranDate.toLocaleTimeString('fa-IR', {
            hour: '2-digit',
            minute: '2-digit',
          });
          const date = iranDate.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
          return [time, date];
        }

        const dateLabel = iranDate.toLocaleDateString('fa-IR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        return [dateLabel];
      }) || [];

    return timestamps;
  };

  const filterDataPoints = dataEntries => {
    if (!dataEntries || dataEntries.length === 0) {
      return [];
    }
    return dataEntries.reduce((acc, currentEntry, index, allEntries) => {
      const currentPrice = currentEntry.averagePrice;
      if (index === 0) {
        acc.push(currentPrice === 0 ? null : currentPrice);
        return acc;
      }
      const previousPrice = allEntries[index - 1].averagePrice;
      if (currentPrice === 0) {
        acc.push(null);
        return acc;
      }
      if (previousPrice > 0) {
        const priceDropRatio = (previousPrice - currentPrice) / previousPrice;
        if (priceDropRatio > 0.3) {
          acc.push(null);
          return acc;
        }
      }
      acc.push(currentPrice);
      return acc;
    }, []);
  };

  const allDatasets = [
    {
      label: 'توکنیکو',
      id: 'SERVAT_TOKENIKO',
      data: filterDataPoints(apiData?.SERVAT_TOKENIKO),
      borderColor: '#C026D3',
      backgroundColor: '#C026D3',
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 3,
    },
    {
      label: 'نارین',
      id: 'SERVAT_NARIN',
      data: filterDataPoints(apiData?.SERVAT_NARIN),
      borderColor: '#16A34A',
      backgroundColor: '#16A34A',
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 3,
    },
    {
      label: 'اتحادیه',
      id: 'ESTJ',
      data: filterDataPoints(apiData?.ESTJ),
      borderColor: '#D97706',
      backgroundColor: '#D97706',
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 3,
    },
    {
      label: '(خرید) بورس کالا',
      id: 'IME_AVG_BID_PRICE',
      data: filterDataPoints(apiData?.IME_AVG_BID_PRICE),
      borderColor: '#2563EB',
      backgroundColor: '#2563EB',
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 3,
    },
    {
      label: '(فروش) بورس کالا',
      id: 'IME_AVG_ASK_PRICE',
      data: filterDataPoints(apiData?.IME_AVG_ASK_PRICE),
      borderColor: '#DC2626',
      backgroundColor: '#DC2626',
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 3,
    },
  ];

  const chartData = {
    labels: getChartLabels(),
    datasets: allDatasets.filter(dataset => sources.includes(dataset.id)),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "'Vazir', sans-serif",
            size: window.innerWidth < 600 ? 10 : 12,
          },
          color: '#6B7280',
          boxWidth: window.innerWidth < 600 ? 15 : 20,
        },
      },
      tooltip: {
        backgroundColor: '#1E3A8A',
        titleFont: { family: "'Vazir', sans-serif", size: 12 },
        bodyFont: { family: "'Vazir', sans-serif", size: 12 },
        callbacks: {
          label: context => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${Number(
              value.toFixed()
            ).toLocaleString('fa-IR', {
              useGrouping: true,
            })}R`;
          },
        },
      },
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
        limits: { x: { min: 'original', max: 'original' } },
      },
    },
    scales: {
      x: {
        type: 'category',
        grid: { display: false },
        ticks: {
          font: { family: "'Vazir', sans-serif" },
          color: '#6B7280',
          maxTicksLimit: window.innerWidth < 600 ? 6 : 10,
          autoSkip: true,
          minRotation: 30,
          maxRotation: 90,
        },
      },
      y: {
        type: 'linear',
        grid: { color: '#E5E7EB' },
        ticks: {
          font: { family: "'Vazir', sans-serif" },
          color: '#6B7280',
          callback: value =>
            `${Number(value.toFixed()).toLocaleString('fa-IR', {
              useGrouping: true,
            })}R`,
        },
        beginAtZero: false,
      },
    },
  };

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: { xs: '12px', md: '16px' },
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        backdropFilter: 'blur(8px) saturate(180%)',
        p: { xs: 2, sm: 2.5, md: 3 },
        transition: 'box-shadow 0.3s ease',
        '&:hover': { boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        mb={{ xs: 2, md: 3 }}
        gap={{ xs: 2, md: 1 }}
      >
        <Typography
          variant={{ xs: 'h6', sm: 'h6', md: 'h5' }}
          sx={{
            fontWeight: 700,
            color: '#111827',
            letterSpacing: '0.5px',
            textAlign: { xs: 'left', sm: 'center', md: 'center' },
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          {!hideWeightInput && (
            <FormControl
              size="small"
              sx={{
                flex: '1 1 120px',
                minWidth: 120,
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  fontFamily: "'Vazir', sans-serif",
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                },
              }}
            >
              <InputLabel
                sx={{
                  fontFamily: "'Vazir', sans-serif",
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  color: '#6B7280',
                  direction: 'rtl',
                }}
              >
                <Scale sx={{ fontSize: 16, verticalAlign: 'middle', ml: 1 }} />
                وزن
              </InputLabel>
              <Select
                value={selectedWeight}
                onChange={handleWeightChange}
                sx={{
                  fontFamily: "'Vazir', sans-serif",
                  color: '#1E3A8A',
                  textAlign: 'right',
                }}
              >
                {availableWeights.map(weight => (
                  <MenuItem
                    key={weight.id}
                    value={weight.id}
                    sx={{
                      fontFamily: "'Vazir', sans-serif",
                      fontSize: '0.9rem',
                      color: '#6B7280',
                      direction: 'rtl',
                    }}
                  >
                    {weight.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            size="small"
            sx={{
              flex: '1 1 120px',
              minWidth: 120,
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                fontFamily: "'Vazir', sans-serif",
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
              },
            }}
          >
            <InputLabel
              sx={{
                fontFamily: "'Vazir', sans-serif",
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                color: '#6B7280',
                direction: 'rtl',
              }}
            >
              <AccessTime
                sx={{ fontSize: 16, verticalAlign: 'middle', ml: 1 }}
              />
              بازه زمانی
            </InputLabel>
            <Select
              value={selectedTimeFrame}
              onChange={handleTimeFrameChange}
              sx={{
                fontFamily: "'Vazir', sans-serif",
                color: '#1E3A8A',
                textAlign: 'right',
              }}
            >
              {timeFrames.map(timeFrame => (
                <MenuItem
                  key={timeFrame.id}
                  value={timeFrame.id}
                  sx={{
                    fontFamily: "'Vazir', sans-serif",
                    fontSize: '0.9rem',
                    color: '#6B7280',
                    direction: 'rtl',
                  }}
                >
                  {timeFrame.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>

      <Box
        sx={{
          height: { xs: '300px', sm: '350px', md: '400px', lg: '450px' },
          p: { xs: 1, md: 2 },
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          direction: 'ltr',
        }}
      >
        {loading ? (
          <Typography
            sx={{
              fontFamily: "'Vazir', sans-serif",
              textAlign: 'center',
              color: '#6B7280',
            }}
          >
            در حال بارگذاری...
          </Typography>
        ) : !apiData || !Object.keys(apiData).length ? (
          <Typography
            sx={{
              fontFamily: "'Vazir', sans-serif",
              textAlign: 'center',
              color: '#6B7280',
            }}
          >
            داده‌ای برای نمایش وجود ندارد
          </Typography>
        ) : (
          <Line ref={chartRef} options={chartOptions} data={chartData} />
        )}
      </Box>
    </Box>
  );
}
